import React from 'react'
import PropTypes from 'prop-types'
import {useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CMSMegaMenuContent} from './cms-mega-menu-content'
import {CMSIllustrationOnlyContent} from './cms-illustration-only-content'
import {CMSCustomLinksContent} from './cms-custom-links-content'
import {DefaultMegaMenuContent} from './default-mega-menu-content'

/**
 * Main ListMenuContent component that switches between CMS and API-driven content.
 *
 * Dispatch order:
 *  1. New CMS MegaMenu (page-level `MegaMenu` block with `ListOfLinksSource`):
 *       - 'CustomLinks'        → CMSCustomLinksContent (page-level CustomListOfLinks pool)
 *       - 'IllustrationsOnly'  → CMSIllustrationOnlyContent (page-level ContentTile.Illustrations)
 *       - 'FromSfcc'           → DefaultMegaMenuContent (SFCC tree, ContentTile illustrations appended)
 *  2. Legacy CMS MegaMenuItemLevelOneBlock (attached via HeaderReference.CategoryReplacement).
 *  3. Default SFCC-driven mega menu.
 */
const ListMenuContent = ({ maxColumns, item, itemsKey, onClose, initialFocusRef, categories }) => {
    const theme = useTheme()
    const { baseStyle } = theme.components.ListMenu

    // ── New page-level CMS MegaMenu config ──
    const cmsMegaMenuMode = item?.cmsMegaMenuMode || null

    if (cmsMegaMenuMode === 'CustomLinks') {
        return (
            <CMSCustomLinksContent
                item={item}
                onClose={onClose}
                baseStyle={baseStyle}
                categories={categories}
            />
        )
    }

    if (cmsMegaMenuMode === 'IllustrationsOnly') {
        // Reuse the existing illustration-only renderer by feeding it the
        // new ContentTile.Illustrations through the same shape it expects.
        const adaptedItem = {
            ...item,
            CMSMegaMenuData: {
                ...(item?.CMSMegaMenuData || {}),
                MegaMenuIlustrationBlockList: item?.cmsContentTileIllustrations || []
            }
        }
        return (
            <CMSIllustrationOnlyContent
                item={adaptedItem}
                baseStyle={baseStyle}
                onClose={onClose}
            />
        )
    }

    if (cmsMegaMenuMode === 'FromSfcc') {
        // Default SFCC mega menu, but inject the page-level ContentTile.Illustrations
        // as the illustration source (overrides legacy MegaMenuIlustrationBlockList
        // only when ContentTile illustrations are present).
        const cmsIllustrations = item?.cmsContentTileIllustrations
        const adaptedItem =
            Array.isArray(cmsIllustrations) && cmsIllustrations.length > 0
                ? { ...item, MegaMenuIlustrationBlockList: cmsIllustrations }
                : item
        return (
            <DefaultMegaMenuContent
                item={adaptedItem}
                itemsKey={itemsKey}
                onClose={onClose}
                baseStyle={baseStyle}
                categories={categories}
            />
        )
    }

    // ── Legacy CMS-driven mega menu (MegaMenuItemLevelOneBlock) ──
    const hasCMSMegaMenu = item?.hasCMSMegaMenu && item?.CMSMegaMenuData
    const hasCMSColumns = item?.CMSMegaMenuData?.MegaMenuItemsLevelOne?.length > 0
    const hasCMSIllustrationsOnly = hasCMSMegaMenu &&
        !hasCMSColumns &&
        item?.CMSMegaMenuData?.MegaMenuIlustrationBlockList?.length > 0

    // Case 1: CMS mega menu with columns
    if (hasCMSMegaMenu && hasCMSColumns) {
        return (
            <CMSMegaMenuContent
                item={item}
                onClose={onClose}
                baseStyle={baseStyle}
                categories={categories}
            />
        )
    }

    // Case 2: CMS mega menu with only illustrations (no columns)
    if (hasCMSIllustrationsOnly) {
        return (
            <CMSIllustrationOnlyContent
                item={item}
                baseStyle={baseStyle}
                onClose={onClose}
            />
        )
    }

    // Case 3: Default API-driven mega menu
    return (
        <DefaultMegaMenuContent
            item={item}
            itemsKey={itemsKey}
            onClose={onClose}
            baseStyle={baseStyle}
            categories={categories}
        />
    )
}

ListMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    maxColumns: PropTypes.number,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    categories: PropTypes.object
}

export { ListMenuContent }
